// ink-component
/* eslint-disable */
import React, {
  ChangeEvent,
  ClipboardEvent,
  FocusEvent,
  KeyboardEvent,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import { ThemeContext } from 'styled-components';

import Control from '../Control/Control';
import { bemHelper, unmask } from '../../helpers';

import InputNumberFormat from '../_common/InputNumberFormat/InputNumberFormat';
import maskWarning from './maskWarning';
import setFocus from '../../helpers/dom/setFocus';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';

import useDeprecatedWarning from '../../hooks/useDeprecatedWarning';
import getGlobalProps from '../../helpers/constants/global-props';

export type MaskType = string | 'ein' | 'ssn' | 'money' | 'phone' | 'decimal' | 'integer' | 'zipcode' | 'percent';

export type Mask = {
  thousandSeparator?: string;
  decimalSeparator?: string;
  decimalScale?: number;
  fixedDecimalScale?: boolean;
  allowEmptyFormatting?: boolean;
  allowNegative?: boolean;
  isNumericString?: boolean;
};

type Meta = {
  initial?: string | number;
  isInvalid?: boolean;
};

type InputType = {
  onFocus?: (value: string | number) => void;
  onChange?: (value: string | number) => void;
  onBlur?: (value: string | number) => void;
  onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
  onCopy?: (e: ClipboardEvent<HTMLInputElement>) => void;
  value?: string | number;
};

export type Round = (value: number | string) => number;

export interface InputProps {
  /** Unique identifier */
  id?: string;
  /** Unique name */
  name?: string;
  /** External control and initial value */
  value?: string | number;
  /** Marks a field as private to keep trackers from logging user input */
  dataPrivate?: string | boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Placeholder for the input */
  placeholder?: string;
  /** Set [autoComplete](/patterns/forms/#autocomplete) */
  autoComplete?: string;
  /** onFocus callback. Passes the value */
  onFocus?: (value: string | number) => void;
  /** onChange callback. Passes the value */
  onChange?: (value: string | number) => void;
  /** onBlur callback. Passes the value */
  onBlur?: (value: string | number) => void;
  /** onKeyDown callback. Passes the Event */
  onKeyDown?: (e) => void;
  /** onPaste callback */
  onPaste?: (e) => void;
  /** onCopy callback */
  onCopy?: (e) => void;
  /** Size modifier */
  size?: 'full' | 'large' | 'half' | 'quarter';
  /** Modifier for right align */
  right?: boolean;
  /** Modifier for tables */
  isCell?: boolean;
  /** Modifier for inline */
  isInline?: boolean;
  /** Modifier for not valid state */
  isInvalid?: any;
  /** Modifier for disabled state */
  isDisabled?: boolean;
  /** Flag that defines if the callback values will be sent unmasked */
  unmaskCallbacks?: boolean;
  /** Defines the max value for numbers */
  maxValue?: number;
  /** Defines a mask. Can be a random pattern string (e.g: ##/##/#### for phones),
   * or our predefined masks (e.g.: 'phone' or 'money'),
   *  or even a custom object (e.g.: { allowNegative: false )  */
  mask?: Mask | MaskType;
  /** In cases of decimal masks (isMoney, isDecimal) you can
   * define a decimal limit of digits */
  decimalLimit?: number;
  /** Prefix content */
  prefix?: ReactNode;
  /** Suffix content */
  suffix?: ReactNode;
  /** HTML maxLength */
  maxLength?: number;
  /** HTML type */
  type?: string;
  /** HTML autoFocus */
  autoFocus?: boolean;
  /** Defines the rounding. It can be either a custom function that changes
   * the input value, a predefined string (round, ceil or floor)  */
  round?: 'round' | 'ceil' | 'floor' | Round;
  /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
  'aria-label'?: string;
  /** @ignore */
  defaultValue?: string | number;
  /** @ignore */
  isInteger?: boolean;
  /** @ignore */
  isMoney?: boolean;
  /** @ignore */
  isSSN?: boolean;
  /** @ignore */
  isDecimal?: boolean;
  /** @ignore */
  isEIN?: boolean;
  /** @ignore */
  isPhone?: boolean;
  /** @ignore */
  isZipCode?: boolean;
  /** @ignore */
  meta?: Meta;
  /** @ignore */
  input?: InputType;
  /** @ignore */
}

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @aka textfield
 * @categories form
 * @purpose Allows users to provide one piece of data
 * @deprecated This component is deprecated and replaced by [NewInput](https://ink.carta.com/components/NewInput/)
 * @status deprecated
 * @replacementComponent NewInput
 * @dateOfDeletion September 1st, 2023
 */
function Input({
  id,
  value = '',
  name = undefined,
  defaultValue = '',
  placeholder = ' ',
  autoComplete = 'off',
  size = undefined,
  right = false,
  prefix = null,
  suffix = null,
  isCell = false,
  isInline = false,
  isInvalid = false,
  isDisabled = false,
  dataPrivate = undefined,
  decimalLimit = 2,
  isInteger = false,
  maxValue = undefined,
  isMoney = false,
  isDecimal = false,
  isSSN = false,
  isEIN = false,
  isPhone = false,
  isZipCode = false,
  mask = undefined,
  unmaskCallbacks = false,
  onBlur = () => {},
  onFocus = () => {},
  onChange = () => {},
  onKeyDown = () => {},
  onPaste = () => {},
  onCopy = () => {},
  maxLength = undefined,
  type = 'text',
  autoFocus = false,
  round = undefined,
  'aria-label': ariaLabel,

  meta = {},
  input = {},
  ...otherProps
}: InputProps) {
  useDeprecatedWarning({ componentName: 'Input', substitute: 'NewInput' });

  maskWarning(mask);

  function processInitialValue(value: string | number | undefined): string | number | undefined {
    /**
     * Input does better with strings. When the initialValue is the falsy number 0,
     * it would otherwise get passed over and passed to the next potential initialValue.
     * However, we still want to show the value 0 in the input field.
     * We can overcome this by casting numbers to a string.
     * */
    if (isNil(value)) return '';
    return !isNaN(Number(value)) ? String(value) : value;
  }

  const initialValue =
    processInitialValue(input.value) || processInitialValue(value) || processInitialValue(meta.initial) || defaultValue;
  const [valueState, setValue] = useState(initialValue);

  useDidUpdateEffect(() => {
    if (!isNil(input.value)) {
      setValue(input.value);
    } else if (!isNil(value)) {
      setValue(value);
    }
  }, [input.value || value]);

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) setFocus(inputRef);
  }, [autoFocus]);

  function getRound(value: number | string): string | number {
    if (typeof round === 'function') {
      return round(value);
    }

    // @ts-ignore
    const roundingFunction = Math[round];

    if (typeof roundingFunction === 'function') {
      return roundingFunction(Number(value) * 100) / 100;
    }

    return value;
  }

  function hasMask(): Mask | boolean | string {
    return mask || isInteger || isMoney || isSSN || isDecimal || isEIN || isPhone || isZipCode;
  }

  function hasTabularMask(): boolean {
    return mask === 'integer' || mask === 'money' || isInteger || isMoney;
  }

  function defineClassName(): string {
    const b = bemHelper();

    return b('ink-control--input', {
      cell: isCell,
      tabular: hasTabularMask(),
      right,
    });
  }

  function defineCallbackValue(value: string | number): string | number {
    return unmaskCallbacks && hasMask() ? unmask(value) : value;
  }

  function handleFocus(e: FocusEvent<HTMLInputElement>): void {
    if (input.onFocus && e.target && e.target.value) {
      input.onFocus(defineCallbackValue(e.target.value));
    }
    onFocus(defineCallbackValue(e.target.value));
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    if (valueState !== e.target.value) {
      if (input.onChange) {
        input.onChange(defineCallbackValue(e.target.value));
      }
      onChange(defineCallbackValue(e.target.value));
    }

    setValue(e.target.value);
  }

  function handleValueChange({ formattedValue, floatValue }) {
    if (valueState !== formattedValue) {
      if (input.onChange) {
        input.onChange(defineCallbackValue(formattedValue));
      }
      onChange(defineCallbackValue(formattedValue));
    }

    setValue(maxValue && floatValue > maxValue ? '' : formattedValue);
  }

  function handleBlur(e: FocusEvent<HTMLInputElement>) {
    const valueWithRounding = getRound(e.target.value);

    if (input.onBlur) {
      input.onBlur(defineCallbackValue(valueWithRounding));
    }
    onBlur(defineCallbackValue(valueWithRounding));

    setValue(valueWithRounding);
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    if (input.onPaste) {
      input.onPaste(e);
    }
    onPaste(e);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    onKeyDown(e);
  }

  function handleCopy(e: ClipboardEvent<HTMLInputElement>) {
    if (input.onCopy) {
      input.onCopy(e);
    }
    onCopy(e);
  }

  const themeContext = useContext(ThemeContext);

  return (
    <Control
      inputId={String(id)}
      size={size}
      isCell={isCell}
      isInvalid={meta.isInvalid || isInvalid}
      isInline={isInline}
      isDisabled={isDisabled}
      prefix={prefix}
      suffix={suffix}
      dataPrivate={dataPrivate}
      data-testid={otherProps['data-testid']}
    >
      {hasMask() ? (
        <InputNumberFormat
          id={id}
          name={name}
          value={valueState}
          className={defineClassName()}
          onFocus={handleFocus}
          onValueChange={handleValueChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onCopy={handleCopy}
          placeholder={placeholder}
          isDisabled={isDisabled}
          autoComplete={autoComplete}
          maxLength={maxLength}
          type={type}
          autoFocus={autoFocus}
          maxValue={maxValue}
          mask={mask}
          decimalLimit={decimalLimit}
          isInteger={isInteger}
          isMoney={isMoney}
          isSSN={isSSN}
          isDecimal={isDecimal}
          isEIN={isEIN}
          isPhone={isPhone}
          isZipCode={isZipCode}
          aria-required={themeContext?.fieldRequired}
          aria-label={ariaLabel}
          {...globalProps}
        />
      ) : (
        <input
          id={id}
          ref={inputRef}
          name={name}
          value={valueState}
          className={defineClassName()}
          onFocus={handleFocus}
          onChange={handleChange}
          onCopy={handleCopy}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          disabled={isDisabled}
          autoComplete={autoComplete}
          maxLength={maxLength}
          type={type}
          autoFocus={autoFocus}
          aria-required={themeContext?.fieldRequired}
          aria-label={ariaLabel}
          {...globalProps}
        />
      )}
    </Control>
  );
}

Input.defaultProps = {
  id: undefined,
  value: '',
  name: undefined,
  defaultValue: '',
  placeholder: ' ',
  autoComplete: 'off',
  size: undefined,
  right: false,
  prefix: undefined,
  suffix: undefined,
  isCell: false,
  isInline: false,
  isInvalid: false,
  isDisabled: false,
  dataPrivate: undefined,
  'data-testid': undefined,
  decimalLimit: 2,
  isInteger: false,
  maxValue: undefined,
  isMoney: false,
  isDecimal: false,
  isSSN: false,
  isEIN: false,
  isPhone: false,
  isZipCode: false,
  mask: undefined,
  unmaskCallbacks: false,
  onBlur: () => {},
  onFocus: () => {},
  onChange: () => {},
  onKeyDown: () => {},
  onPaste: () => {},
  onCopy: () => {},
  maxLength: undefined,
  type: 'text',
  autoFocus: false,
  round: undefined,

  meta: {},
  input: {},
};

Input.displayName = 'Input';

export default Input;
